import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import DropzoneRequestsContext from './DropzoneRequests.context';
import { DropzoneFile } from 'components/Dropzone/utils';
import { DropzoneFieldRequests } from 'hooks/useDropzoneField';
import PopNotificationsContext from 'providers/PopNotifications/PopNotifications.context';
import { DropzoneDelete, DropzoneUpload } from 'models/Dropzone';

export type DropzoneRequestsProps = DropzoneFieldRequests;

const DropzoneRequests: React.FC<DropzoneRequestsProps> = (props) => {
  const { children, fetchFiles, uploadRequest, deleteRequest } = props;
  const [files, setFiles] = useState<Array<DropzoneFile>>([]);
  const { popServerError } = useContext(PopNotificationsContext);

  const uploadHandle: DropzoneUpload = useCallback(
    async (file, fileName, config) => {
      try {
        const savedFile = await uploadRequest(file, fileName, config);
        setFiles((old) => [...(old || []), savedFile]);

        return savedFile;
      } catch (e) {
        popServerError(e);
      }
    },
    [popServerError, uploadRequest],
  );

  const deleteHandle: DropzoneDelete = useCallback(
    async (file, fileName) => {
      try {
        const deletedId = await deleteRequest(file, fileName);
        setFiles((old) => old?.filter((file) => file.id !== deletedId));
        return deletedId;
      } catch (e) {
        popServerError(e);
      }
    },
    [deleteRequest, popServerError],
  );

  useEffect(() => {
    (async () => setFiles(await fetchFiles?.()))();
  }, [fetchFiles, setFiles]);

  const dropzoneProviderValue = useMemo(
    () => ({
      uploadRequest: uploadHandle,
      deleteRequest: deleteHandle,
      files,
      updateFiles: setFiles,
    }),
    [deleteHandle, files, setFiles, uploadHandle],
  );

  return (
    <DropzoneRequestsContext.Provider value={dropzoneProviderValue}>
      {children}
    </DropzoneRequestsContext.Provider>
  );
};

export default DropzoneRequests;
